import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { authFetch } from "@/lib/auth";
import { toast } from "sonner";
interface QuickLogSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
export function QuickLogSheet({ open, onOpenChange }: QuickLogSheetProps) {
  const [logType, setLogType] = useState<string>("maintenance");
  const [entityType, setEntityType] = useState("");
  const [entityName, setEntityName] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) {
      toast.error("Description is required");
      return;
    }
    setSubmitting(true);
    try {
      await authFetch("/api/logs", {
        method: "POST",
        body: JSON.stringify({
          log_type: logType,
          entity_type: entityType || "general",
          entity_id: "",
          entity_name: entityName || "General",
          description: description.trim(),
          date,
          metadata: {},
        }),
      });
      toast.success("Log entry created");
      setDescription("");
      setEntityType("");
      setEntityName("");
      onOpenChange(false);
    } catch (err) {
      console.error("[QuickLog]", err);
      toast.error("Failed to create log entry");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Quick Log Entry</SheetTitle>
          <SheetDescription>Quickly record an activity or event.</SheetDescription>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ql-type">Log Type</Label>
            <Select value={logType} onValueChange={setLogType}>
              <SelectTrigger id="ql-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="maintenance">Maintenance</SelectItem>
                <SelectItem value="pasture">Pasture</SelectItem>
                <SelectItem value="vet">Vet</SelectItem>
                <SelectItem value="animal-care">Animal Care</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="ql-entity-type">Entity Type</Label>
            <Input
              id="ql-entity-type"
              placeholder="e.g., vehicle, animal, pasture"
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ql-entity-name">Entity Name</Label>
            <Input
              id="ql-entity-name"
              placeholder="e.g., Big Red, Bessie"
              value={entityName}
              onChange={(e) => setEntityName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ql-date">Date</Label>
            <Input
              id="ql-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ql-desc">Description *</Label>
            <Textarea
              id="ql-desc"
              placeholder="What happened?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90"
          >
            {submitting ? "Saving..." : "Save Log Entry"}
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}